import { useEffect, useState } from 'react';

import { useSelector } from 'react-redux';

import {
  buildEmptyStateHintKey,
  readEmptyStateHint,
  writeEmptyStateHint,
} from '@/lib/storage/emptyStateHints';
import { selectAccountSlug } from '@/redux/session/selectors';

const SETTLE_MS = 250;

interface EmptyStateViewOptions {
  scope: string;
  isLoading: boolean;
  isEmpty: boolean;
  isReady?: boolean;
  isError?: boolean;
}

interface EmptyStateView {
  showEmpty: boolean;
  showEmptySkeleton: boolean;
  showList: boolean;
  isEmptyPage: boolean;
}

export const useEmptyStateView = ({
  scope,
  isLoading,
  isEmpty,
  isReady = true,
  isError = false,
}: EmptyStateViewOptions): EmptyStateView => {
  const isPending = isLoading || !isReady;
  const accountSlug = useSelector(selectAccountSlug);
  const key = buildEmptyStateHintKey(accountSlug, scope);

  const [hint, setHint] = useState(() => ({ key, value: readEmptyStateHint(key) }));
  if (hint.key !== key) {
    setHint({ key, value: readEmptyStateHint(key) });
  }

  const [isSettling, setIsSettling] = useState(isPending && hint.value !== false);

  useEffect(() => {
    if (!isSettling) {
      return undefined;
    }
    if (!isPending) {
      setIsSettling(false);
      return undefined;
    }

    const timer = setTimeout(() => setIsSettling(false), SETTLE_MS);
    return () => clearTimeout(timer);
  }, [isPending, isSettling]);

  useEffect(() => {
    if (isPending || isError) {
      return;
    }
    writeEmptyStateHint(key, isEmpty);
    setHint({ key, value: isEmpty });
  }, [isEmpty, isError, isPending, key]);

  const showEmpty = !isPending && isEmpty;
  const expectEmpty = isPending && isReady && hint.value === true;

  return {
    showEmpty,
    showEmptySkeleton: expectEmpty && !isSettling,
    showList: !showEmpty && !expectEmpty && !isSettling,
    isEmptyPage: showEmpty || expectEmpty,
  };
};
